import { Controller, type Control } from 'react-hook-form';
import { Box, Grid, TextField } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';
import Typography from '@mui/material/Typography';
import type { EditActiveOrganization } from '../../edit.modal';
import DeleteOrganization from './delete-organization';
import { useGetOrganizationRelationsQuery } from '@/state/queries/organizations/use-get-organization-relations.ts';
import { ORGANIZATION_ROLES } from '@/enums/organization-roles.enum.ts';
import { useMemo } from 'react';

export default function GeneralForm({
  control,
  close,
}: {
  control: Control<EditActiveOrganization>;
  close: React.Dispatch<React.SetStateAction<void>>;
}) {
  const t = useTranslations();
  const activeOrganization = useActiveOrganizationStore((s) => s.activeOrganization);
  const { data: ownerRelations, isLoading: ownersLoading } = useGetOrganizationRelationsQuery(activeOrganization?.id, {
    elevatedRole: ORGANIZATION_ROLES.OWNER,
    status: 'active',
  });

  const ownersDisplay = useMemo(() => {
    const rows = ownerRelations?.data ?? [];
    if (rows.length === 0) {
      return null;
    }
    return rows
      .map((row) => {
        const name = [row.user?.firstName, row.user?.lastName].filter(Boolean).join(' ');
        return name || row.user?.primaryEmail || row.user?.id || '—';
      })
      .join(', ');
  }, [ownerRelations?.data]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: '300px',
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('organization.generalSettings')}
        </Typography>
        <Controller
          name="general.name"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              required
              fullWidth
              data-testid="name"
              type="text"
              variant="standard"
              label={t('organization.name')}
              helperText={fieldState.error ? fieldState.error.message : null}
              error={!!fieldState.error}
            />
          )}
        />
      </Box>

      <Grid container spacing={2} sx={{ mt: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="body1">{t('organization.owner')}:</Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <Typography variant="body1" color="text.disabled">
            {ownersLoading
              ? t('organization.team.loadingOwners')
              : (ownersDisplay ?? t('organization.team.noOwnersListed'))}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="body1">{t('organization.id')}:</Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <Typography variant="body1" color="text.disabled">
            {activeOrganization?.id}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <DeleteOrganization close={close} />
        </Grid>
      </Grid>
    </>
  );
}
